import { toast } from 'react-toastify';
import { PointsUpdatePayload } from 'types/interfaces/points';

// Batch tracking for rapid changes
let pendingToasts: PointsUpdatePayload[] = [];
let batchTimeout: NodeJS.Timeout | null = null;

const BATCH_WINDOW_MS = 2000; // 2 second batching window

/**
 * Format number with sign and commas
 */
const formatChange = (change: number): string => {
  const sign = change > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('en-US').format(change)}`;
};

/**
 * Format a single points update message based on reason type
 */
const formatPointsMessage = (payload: PointsUpdatePayload): string => {
  const { change, reason, details } = payload;
  const formattedChange = formatChange(change);

  switch (reason) {
    case 'trade_completed':
      return details?.username
        ? `Trade completed! ${formattedChange} points from ${details.username}`
        : `Trade completed! ${formattedChange} points`;

    case 'trade_initiated':
      return details?.username
        ? `New cards incoming from ${details.username}!`
        : `New trade incoming!`;

    case 'trade_cards_added':
      return details?.username
        ? `${details.username} added cards to your trade!`
        : `Cards added to your trade!`;

    case 'refund_full':
      return details?.tradeNumber
        ? `${formattedChange} points refunded (Trade #${details.tradeNumber} cancelled)`
        : `${formattedChange} points refunded`;

    case 'trade_item_edit':
      return details?.cardName && details?.username
        ? `${formattedChange} points, trade for ${details.cardName} edited by ${details.username}`
        : `${formattedChange} points (trade item edited)`;

    case 'refund_partial':
      return details?.tradeNumber
        ? `${formattedChange} points refunded from Trade #${details.tradeNumber}`
        : `${formattedChange} points refunded`;

    case 'monthly_fee':
      const discountText = details?.discountApplied ? ' (discount applied)' : '';
      return `Monthly fee: ${formattedChange} points${discountText}`;

    case 'onboarding_reward':
      return details?.taskName
        ? `${formattedChange} points! Task completed: ${details.taskName}`
        : `${formattedChange} points! Task completed`;

    case 'admin_adjustment':
      return `Balance adjusted: ${formattedChange} points`;

    default:
      return `${formattedChange} points`;
  }
};

/**
 * Determine toast type - always success since all point changes are positive UX
 * Even debits (like trade_initiated) mean the user is receiving cards
 */
const getToastType = (): 'success' => {
  return 'success';
};

/**
 * Show the batched toast(s)
 */
const showBatchedToast = (): void => {
  if (pendingToasts.length === 0) return;

  if (pendingToasts.length === 1) {
    // Single toast - show full message
    const payload = pendingToasts[0];
    const message = formatPointsMessage(payload);

    toast(message, {
      type: getToastType(),
      position: 'top-right',
      autoClose: 4000,
      className: 'points-toast',
    });
  } else {
    // Multiple toasts - batch into summary
    const netChange = pendingToasts.reduce((sum, t) => sum + t.change, 0);
    const formattedNet = formatChange(netChange);
    const message = `${pendingToasts.length} transactions: ${formattedNet} points net`;

    toast(message, {
      type: getToastType(),
      position: 'top-right',
      autoClose: 5000,
      className: 'points-toast',
    });
  }

  // Reset batch state
  pendingToasts = [];
  batchTimeout = null;
};

/**
 * Queue a points toast with batching for rapid changes
 * Toasts within 2 seconds of each other are batched into a single notification
 */
export const showPointsToast = (payload: PointsUpdatePayload): void => {
  pendingToasts.push(payload);

  // Start batch timer if not already running
  if (!batchTimeout) {
    batchTimeout = setTimeout(showBatchedToast, BATCH_WINDOW_MS);
  }
};

/**
 * Immediately show any pending toasts (useful for testing or cleanup)
 */
export const flushPointsToasts = (): void => {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }
  showBatchedToast();
};

/**
 * Clear any pending toasts without showing them
 */
export const clearPendingPointsToasts = (): void => {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }
  pendingToasts = [];
};
